/**
 * Full-screen "driving mode" lock shown on the PHONE while Android Auto is
 * connected (projecting to the car).
 *
 * Android for Cars Quality Guidelines require that an app "disable features
 * requiring phone interaction while in driving mode" — i.e. the phone must not
 * present interactive, attention-demanding UI while the car display is active.
 * Pre-v1.0.221 Fricamp deliberately kept the phone fully usable (a small status
 * pill), which Google rejected ("Visual info on phone"). This overlay reverses
 * that: it covers the entire app, swallows all touch input, and shows a static,
 * non-interactive status screen. It disappears automatically the moment Android
 * Auto disconnects (driven by the polled `isAutoConnected` state).
 *
 * Intentionally has NO interactive controls — navigation is controlled from the
 * car screen. The only DOM here is read-only text + an icon.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';

interface DrivingModeOverlayProps {
  /** True while an Android Auto head unit is connected/projecting. */
  isActive: boolean;
  isNavigating?: boolean;
  destinationName?: string;
  remainingMeters?: number;
}

const formatDistance = (m?: number): string | null => {
  if (m == null || !isFinite(m) || m <= 0) return null;
  return m < 1000 ? `${Math.round(m)} m` : `${(m / 1000).toFixed(1)} km`;
};

export const DrivingModeOverlay: React.FC<DrivingModeOverlayProps> = ({
  isActive,
  isNavigating = false,
  destinationName,
  remainingMeters,
}) => {
  const { t } = useTranslation();
  if (!isActive) return null;

  const dist = formatDistance(remainingMeters);

  return (
    <div
      // Top of everything; capture all pointer/touch events so nothing behind
      // the overlay can be interacted with while driving.
      className="fixed inset-0 z-[100000] flex flex-col items-center justify-center gap-6 px-8 text-center select-none"
      style={{
        background: 'radial-gradient(circle at 50% 35%, #14233b 0%, #0a1626 60%, #05080f 100%)',
        touchAction: 'none',
      }}
      onClickCapture={(e) => { e.stopPropagation(); }}
      onTouchStartCapture={(e) => { e.stopPropagation(); }}
      role="status"
      aria-live="polite"
    >
      {/* Car / steering icon */}
      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-amber-500/15 ring-1 ring-amber-400/30">
        <svg width="52" height="52" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="9" />
          <circle cx="12" cy="12" r="2.5" />
          <path d="M12 9.5V3.2M9.6 12.2 4.2 14.8M14.4 12.2l5.4 2.6" />
        </svg>
      </div>

      <div className="space-y-2">
        <h1 className="text-2xl font-bold text-white">
          {t('driving_mode.title', 'Du kjører')}
        </h1>
        <p className="mx-auto max-w-sm text-sm leading-relaxed text-slate-300">
          {t(
            'driving_mode.subtitle',
            'Fricamp styres fra bilskjermen mens Android Auto er tilkoblet. Telefonen er låst av sikkerhetsgrunner.',
          )}
        </p>
      </div>

      {isNavigating && (destinationName || dist) && (
        <div className="mt-2 rounded-2xl bg-white/5 px-5 py-3 ring-1 ring-white/10">
          <p className="text-tiny font-semibold uppercase tracking-wider text-amber-400">
            {t('driving_mode.navigating', 'Navigerer')}
          </p>
          {destinationName && (
            <p className="mt-0.5 text-base font-semibold text-white">{destinationName}</p>
          )}
          {dist && <p className="text-sm text-slate-300">{dist}</p>}
        </div>
      )}

      <p className="absolute bottom-8 left-0 right-0 px-8 text-xs text-slate-500">
        {t('driving_mode.hint', 'Skjermen låses opp automatisk når du kobler fra Android Auto.')}
      </p>
    </div>
  );
};

export default DrivingModeOverlay;
